import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { NotificationType, PaginationMeta, User } from '@app/shared/interfaces';
import { Filter } from '@app/shared/models/filter';
import { FilterService } from '@app/shared/services/filter/filter.service';
import { SegmentService } from '@app/shared/services/filter/segment.service';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { PresenceService } from '@app/shared/services/presence/presence.service';
import { PresenceInfo, PusherService } from '@app/shared/services/pusher/pusher.service';
import { SegmentEvent, SegmentIoService } from '@app/shared/services/segmentIo/segment-io.service';
import { SentimentAnalysisService } from '@app/shared/services/sentiment-analysis/sentiment-analysis.service';
import { ThreadService } from '@app/shared/services/thread/thread.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { Subject, Subscription } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import { DialogBroadcastMessageComponent } from '../../components/dialog-broadcast-message/dialog-broadcast-message.component';

@Component({
  standalone: false,
  selector: 'sbnb-inbox',
  templateUrl: './inbox.component.html',
  styleUrls: ['./inbox.component.scss', './inbox.component.mobile.scss'],
})
export class InboxComponent implements OnInit, OnDestroy {
  maxSelectableItemsForBroadcast = 100;
  private inboxPusherUpdateInterval = 5000;

  loading: boolean;
  inboxItems: any[];
  currentOffset: number | null = null;
  currentCursor: string | null = null;
  activeSegment: string;
  activeSegmentTitle: string;
  activeSegmentUuid: string;
  segmentFilterDefaults: Filter[];
  paginationData: PaginationMeta;
  activeFilters: Filter[] = [];
  searchCriteria: string;
  preapproveLoading: boolean;
  checkedConversations: any[] = [];
  resolvingThreads: boolean;
  unresolvingThreads: boolean;
  resolvedProgressCount = 0;

  user: User;
  perms$ = this.userPermissionsService;
  Features = Features;

  private pusherSubscription: Subscription = null;
  public sentimentAnalysisEnabled$ = this.sentimentAnalysisService.enabled$;

  // Add new properties for presence tracking
  usersViewingList: PresenceInfo[] = [];

  // Add new property to store thread viewers
  private threadViewers = new Map<string, PresenceInfo[]>();

  private destroyed$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private threadService: ThreadService,
    private location: Location,
    private filter: FilterService,
    private segmentService: SegmentService,
    private titleService: TitleService,
    private pusherService: PusherService,
    private dialog: MatDialog,
    private toast: ToastNotificationsService,
    private segmentIoService: SegmentIoService,
    private authService: AuthenticationService,
    private userPermissionsService: UserPermissionsService,
    private posthogService: PosthogService,
    private presenceService: PresenceService,
    private sentimentAnalysisService: SentimentAnalysisService
  ) {}

  ngOnInit() {
    this.user = this.authService.getUserDetails();
    this.loading = true;

    // retrieve record offset from the URL matrix param
    this.currentOffset = +this.route.snapshot.paramMap.get('offset') || 0;
    this.currentCursor = this.route.snapshot.paramMap.get('cursor') || null;

    // Check the URL for segment info, and fetch the appropriate filters
    this.activeSegment = this.route.snapshot.paramMap.get('activeSegment');
    this.searchCriteria = this.route.snapshot.paramMap.get('query');

    if (this.activeSegment) {
      this.segmentService.getSegmentById(this.activeSegment, 'threads').subscribe((res) => {
        this.activeFilters = res.filters;
        this.activeSegmentTitle = res.title;
        this.activeSegmentUuid = res.uuid;
        this.segmentFilterDefaults = this.activeFilters;

        this.titleService.manuallySet(this.activeSegmentTitle);

        // If we stashed filters for this key, retrieve and set them
        const stashedFilters = this.filter.retrieveStashedValuesForKey('threads');
        if (stashedFilters) {
          this.activeFilters = stashedFilters;
          this.segmentFilterDefaults = this.activeFilters;
        }

        this.loadMessages(this.activeFilters, this.currentOffset, this.searchCriteria, this.currentCursor);

        this.listenForInboxPusherUpdates();
      });
    }

    // Subscribe to presence updates
    try {
      if (this.user.beta_features_enabled) {
        // Overall thread list presence (this page)
        this.presenceService
          .getUsersViewingThreadList()
          .pipe(takeUntil(this.destroyed$))
          .subscribe((users) => {
            this.usersViewingList = users;
            console.log('Inbox: Users viewing thread list updated', {
              count: users.length,
              users: users.map((u) => ({ id: u.user_id, name: u.first_name })),
            });
          });

        // Presence per thread
        this.presenceService
          .getUsersViewingThreads()
          .pipe(takeUntil(this.destroyed$))
          .subscribe((threadMap) => {
            this.threadViewers = threadMap;
            console.log('Inbox: Thread viewers map updated', {
              threadCount: threadMap.size,
              viewers: Array.from(threadMap.entries()).map(([threadId, viewers]) => ({
                threadId,
                viewerCount: viewers.length,
                viewers: viewers.map((v) => ({ id: v.user_id, name: v.first_name })),
              })),
            });
          });
      }
    } catch (error) {
      console.error('Error subscribing to presence updates:', error);
    }
  }

  ngOnDestroy(): void {
    if (this.pusherSubscription && this.pusherSubscription.unsubscribe) {
      this.pusherSubscription.unsubscribe();
    }

    // Presence updates will come from backend via Pusher
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  listenForInboxPusherUpdates() {
    this.pusherSubscription = this.pusherService.inboxListUpdate
      .pipe(auditTime(this.inboxPusherUpdateInterval))
      .subscribe(() => {
        console.log('Fetching fresh Inbox threads');
        this.checkForUpdates();
      });
  }

  /* Load Conversation Threads from the Thread Service & Manage Pagination Response */
  private loadMessages(filters: Filter[], offset?: number, search?: string, cursor?: string, suppressLoading = false) {
    if (!suppressLoading) {
      this.loading = true;
    }

    const request$ = this.threadService.getThreads(filters, offset, search, cursor);

    request$.subscribe((res) => {
      // Always replace the items
      this.inboxItems = res.data;

      // Determine pagination type based on response structure
      const pagination = res.meta?.pagination;

      // Reset both pagination types
      this.paginationData = null;

      this.paginationData = pagination;

      this.loading = false;
    });
  }

  public handlePageChanged(newPageNo: number) {
    // We use offsets as the API deals in offsets rather than pages, when requesting data
    const newOffset = newPageNo * this.paginationData.per_page - this.paginationData.per_page;

    this.currentOffset = newOffset;

    // Commit the new offset to the URL - our source of truth for offsets
    const url = this.router
      .createUrlTree(
        [
          {
            offset: newOffset,
            query: this.searchCriteria ? this.searchCriteria : '',
          },
        ],
        {
          relativeTo: this.route,
        }
      )
      .toString();

    this.location.go(url);

    // Reload Conversation Threads based on new offset data, with existing filters
    this.loadMessages(this.activeFilters, this.currentOffset, this.searchCriteria, this.currentCursor);
  }

  public handleCursorChanged(cursor: string) {
    this.currentCursor = cursor;

    // Update URL with new cursor or remove it if null
    const params = this.searchCriteria ? { query: this.searchCriteria } : {};
    if (cursor !== null) {
      params['cursor'] = cursor;
    }

    const url = this.router
      .createUrlTree([params], {
        relativeTo: this.route,
      })
      .toString();

    this.location.go(url);

    this.loadMessages(this.activeFilters, this.currentOffset, this.searchCriteria, this.currentCursor);
  }

  public handleFilterChanged(filters: Filter[]) {
    this.activeFilters = filters;

    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('threads', this.activeFilters);

    // Retrieve conversations based on the new filters
    this.loadMessages(this.activeFilters);

    // Zero out the URL parameters - we have fresh data
    const url = this.router
      .createUrlTree([this.searchCriteria ? { query: this.searchCriteria } : {}], {
        relativeTo: this.route,
      })
      .toString();

    this.location.go(url);
  }

  public preapproveThread(item, event) {
    event.stopPropagation();
    item.preapproveLoading = true;

    this.threadService.preapprove(item.uuid).subscribe((thread) => {
      item.booking.status.description_key = thread.status_key;
      item.booking.status.description = thread.status;
      item.preapproveLoading = false;
    });
  }

  public searchUpdated(searchVal: string) {
    // This is already debounced by the child presentation component
    const url = this.router.createUrlTree([{ query: searchVal }], { relativeTo: this.route }).toString();
    this.location.go(url);
    this.searchCriteria = searchVal;
    this.loadMessages(this.activeFilters, this.currentOffset, this.searchCriteria, this.currentCursor);
  }

  openThread(thread, e) {
    // Stash the filter values - to persist through page reloads
    this.filter.stashValuesForKey('threads', this.activeFilters);

    if (e.ctrlKey || e.metaKey || e.button === 1) {
      window.open(`${window.location.origin}/inbox/thread/${thread.uuid}`);
    } else {
      this.router.navigate(['/inbox/thread', thread.uuid]);
    }
  }

  filterReset() {
    this.filter.stashValuesForKey('threads', []);

    this.router.navigate(['inbox']);
  }

  checkForUpdates() {
    this.loadMessages(this.activeFilters, this.currentOffset, this.searchCriteria, this.currentCursor, true);
  }

  trackInboxItem(index, item) {
    return item.uuid;
  }

  displayMaxThreadSelectedToast() {
    this.toast.open(
      `Mass messaging currently only supports up to ${this.maxSelectableItemsForBroadcast} conversations`,
      'Dismiss',
      NotificationType.Error
    );
  }

  selectConversationcheckboxChecked(event: MatCheckboxChange, thread: any) {
    if (event.checked === true) {
      if (this.checkedConversations.length < this.maxSelectableItemsForBroadcast) {
        this.checkedConversations.push(thread);
      } else {
        event.source.checked = false;
        return false;
        this.displayMaxThreadSelectedToast();
      }
    } else {
      const index = this.checkedConversations.findIndex((th) => th.uuid === thread.uuid);

      if (index !== -1) {
        this.checkedConversations.splice(index, 1);
      }
    }
  }

  selectedExists(item) {
    return this.checkedConversations.find((conv) => conv.uuid === item.uuid);
  }

  areAllThreadsOnPageSelected() {
    if (this.checkedConversations.length < this.inboxItems.length) {
      return false;
    }

    const thisPageItemsUuids = this.inboxItems.map((item) => item.uuid);

    const thisPageItems = this.checkedConversations.filter((cc) => thisPageItemsUuids.includes(cc.uuid));

    return thisPageItems.length === this.inboxItems.length;
  }

  toggleAll(event: MatCheckboxChange) {
    if (event.checked) {
      let hitLimit = false;

      this.inboxItems.forEach((row) => {
        const index = this.checkedConversations.findIndex((conv) => conv.uuid === row.uuid);

        if (index > -1) {
          // skipping, it's already checked
        } else {
          if (this.checkedConversations.length < this.maxSelectableItemsForBroadcast) {
            this.checkedConversations.push(row);
          } else {
            hitLimit = true;
            event.source.checked = false;
          }
        }
      });

      if (hitLimit) {
        this.displayMaxThreadSelectedToast();
      }
    } else {
      this.inboxItems.forEach((row) => {
        const index = this.checkedConversations.findIndex((conv) => conv.uuid === row.uuid);

        if (index > -1) {
          this.checkedConversations.splice(index, 1);
        }
      });
    }
  }

  sendBroadcast() {
    this.segmentIoService.track(SegmentEvent.BroadcastDialogOpened, {});

    this.dialog.open(DialogBroadcastMessageComponent, {
      maxHeight: '90vh',
      maxWidth: '720px',
      data: {
        threads: this.checkedConversations,
      },
    });
  }

  markAsResolved(conversations = this.checkedConversations) {
    this.resolvingThreads = true;
    this.resolvedProgressCount = 0;

    conversations.forEach((conversation) => {
      this.threadService.markAsResolved(conversation.uuid).subscribe((res) => {
        if (res.success) {
          this.resolvedProgressCount++;
        }
      });
    });

    setTimeout(() => {
      this.resolvingThreads = false;
      this.checkedConversations = [];
      this.resolvedProgressCount = 0;
      this.loadMessages(this.activeFilters, this.currentOffset, this.searchCriteria, this.currentCursor);
    }, 2500);
  }

  markAsUnresolved(conversations = this.checkedConversations) {
    this.unresolvingThreads = true;
    this.resolvedProgressCount = 0;

    conversations.forEach((conversation) => {
      this.threadService.markAsUnresolved(conversation.uuid).subscribe((res) => {
        if (res.success) {
          this.resolvedProgressCount++;
        }
      });
    });

    setTimeout(() => {
      this.unresolvingThreads = false;
      this.checkedConversations = [];
      this.resolvedProgressCount = 0;
      this.loadMessages(this.activeFilters, this.currentOffset, this.searchCriteria, this.currentCursor);
    }, 2500);
  }

  onManualBookingChanged() {
    this.loadMessages(this.activeFilters, this.currentOffset, this.searchCriteria, this.currentCursor);
  }

  getUsersViewingThread(threadId: string | undefined): PresenceInfo[] {
    if (!threadId) return [];
    return this.threadViewers.get(threadId) || [];
  }
}
